import AntForm, { type AntSchema, useAntForm } from '@9troisquarts/ant-form'
import { Alert, ConfigProvider, Drawer, Layout, Typography } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import { type CustomerType } from '../../types'
import { castAttributesFromDefinition, type ModelDefinitionType } from '@9troisquarts/wand'
import post from '../../../utils/httpPost'
import { getLocaleUi, qualimetrieWebsiteUrl, type SupportedLocale } from '../../../utils/localeRegistry'
import useI18n from '../../../utils/useI18n'
// @ts-expect-error
import whiteLogo from '../../../../assets/images/Qualimetrie-white-logo.svg'
// @ts-expect-error
import logo from '../../../../assets/images/Qualimetrie-logo.svg'
import SignUp from './SignUp'
import TranslationProvider from '../../../utils/TranslationProvider'
import ResetPassword from './ResetPassword'
import useWindowDimensions from '../../../utils/useWindowDimensions'
import { configureLocationField } from '../../../utils/configureLocationField'
import { configureMarketUi } from '../../../utils/marketUi'
import { Button, Eyebrow } from '@/react/front/components/primitives'

interface SignInProps {
  customer: CustomerType
  flash?: any
  locale: SupportedLocale
  nationalIdRequired?: boolean
}

const extractFlashMessage = (flash: any): string => {
  if (!flash) return ''
  if (typeof flash === 'string') return flash

  if (Array.isArray(flash)) {
    const alertEntry = flash.find((entry: any) =>
      Array.isArray(entry) && (entry[0] === 'alert' || entry[0] === 'error')
    )
    if (alertEntry) return String(alertEntry[1] || '')
    const first = flash[0]
    if (typeof first === 'string') return first
    return ''
  }

  if (typeof flash === 'object') {
    return String(flash.alert || flash.error || '')
  }

  return ''
}

const COMPACT_LAYOUT_MAX_WIDTH = 900

const PanelHeading: React.FC<{ eyebrow: string, title: string }> = ({ eyebrow, title }) => (
  <div className='home-panel-heading'>
    <Eyebrow className='home-panel-eyebrow'>{eyebrow}</Eyebrow>
    <Typography.Title level={4} className='home-panel-title'>
      {title}
    </Typography.Title>
  </div>
)

const customerDefinition: ModelDefinitionType = {
  email: 'String',
  password: 'Password'
}

const SignIn: React.FC<SignInProps> = props => {
  const { width } = useWindowDimensions()
  const isMobile = width < 768
  const isCompact = width < COMPACT_LAYOUT_MAX_WIDTH
  const {
    customer,
    flash,
    locale,
    nationalIdRequired = true
  } = props

  configureMarketUi({ nationalIdRequired })

  const localeUi = getLocaleUi(locale)
  const { t } = useI18n()
  const [panel, setPanel] = useState<'signIn' | 'resetPassword'>('signIn')
  const [signUpOpen, setSignUpOpen] = useState(false)
  const [mobileFormOpen, setMobileFormOpen] = useState(() => Boolean(extractFlashMessage(flash)))
  const [error, setError] = useState<string>('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | undefined>(undefined)
  const { object, onChange } = useAntForm(customer || {})

  useEffect(() => {
    configureLocationField(locale)
  }, [locale])

  useEffect(() => {
    const message = extractFlashMessage(flash)
    if (!message) return

    setError(message)
    setPanel('signIn')
    setSignUpOpen(false)
    setMobileFormOpen(true)
  }, [flash])

  const revealAuthPanel = (next: 'signIn' | 'resetPassword' = 'signIn') => {
    setPanel(next)
    setSignUpOpen(false)
    setMobileFormOpen(true)
    if (next !== 'signIn') {
      setError('')
      setFieldErrors(undefined)
    }
  }

  const hideMobileForm = () => {
    setMobileFormOpen(false)
    setPanel('signIn')
  }

  useEffect(() => {
    if (!isCompact || !mobileFormOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideMobileForm()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => { window.removeEventListener('keydown', onKeyDown) }
  }, [isCompact, mobileFormOpen])

  const openSignUp = () => {
    setSignUpOpen(true)
    setError('')
    setFieldErrors(undefined)
  }

  const closeSignUp = () => {
    setSignUpOpen(false)
  }

  const handleChange = (...args: any[]) => {
    onChange(...args)
    if (error) setError('')
    if (fieldErrors) setFieldErrors(undefined)
  }

  const onSubmit = () => {
    const email = String(object.email || '').trim()
    const password = String(object.password || '')

    if (!email || !password) {
      setError(t('sentences.auth_fields_required'))
      setFieldErrors({
        ...(email ? {} : { email: [t('sentences.auth_field_required')] }),
        ...(password ? {} : { password: [t('sentences.auth_field_required')] })
      })
      return
    }

    setFieldErrors(undefined)
    post('/customers/sign_in', castAttributesFromDefinition(customerDefinition, object), 'customer')
  }

  const SignInSchema: AntSchema = [
    {
      name: 'email',
      label: t('words.email'),
      required: true,
      input: {
        type: 'string',
        inputProps: {
          placeholder: 'paul.maria@gmail.com',
          autoComplete: 'email'
        }
      },
      colProps: { xs: 24, md: 24 }
    },
    {
      name: 'password',
      label: t('words.password'),
      required: true,
      input: {
        type: 'password',
        inputProps: {
          placeholder: '******',
          autoComplete: 'current-password'
        }
      },
      colProps: { xs: 24, md: 24 }
    },
    <div key='password-reset-link' className='forgot-row'>
      <button type='button' className='auth-text-link' onClick={() => { revealAuthPanel('resetPassword') }}>
        {t('sentences.forgotten_password')}
      </button>
    </div>
  ]

  return (
    <ConfigProvider
      locale={localeUi.antd}
      form={{
        validateMessages: {
          required: t('sentences.auth_field_required')
        }
      }}
    >
      <Layout className='home-layout'>
        <div className='home-container'>
          <header className='home-header'>
            <div className='home-logo'>
              <button
                type='button'
                className='home-logo-button'
                onClick={() => {
                  if (isCompact && mobileFormOpen) {
                    hideMobileForm()
                    return
                  }
                  revealAuthPanel('signIn')
                }}
              >
                <img src={whiteLogo} width={203} height={113} alt="Qualimetrie" />
              </button>
            </div>
            <div className='home-actions'>
              <Button
                id='signUpButton'
                variant="outline"
                className='outlined-home-btn'
                onClick={openSignUp}
              >
                {t('words.to_sign_up')}
              </Button>
            </div>
          </header>

          <div className='home-split'>
            <section className='home-hero'>
              <Eyebrow className='home-eyebrow'>{t('dashboard.shopperSpace')}</Eyebrow>
              <h1>{t('sentences.welcome_to_qualimetrie')}</h1>
              <p className='home-lead'>{t('sentences.wish_to_become_customer')}</p>
              <p>{t('sentences.home_text_block')}</p>
              <a
                className='qualimetrie-link'
                href={qualimetrieWebsiteUrl(locale)}
                target='_blank'
                rel="noreferrer"
              >
                {t('sentences.discover_qualimetrie')}
              </a>
              <div className={`home-hero-login${mobileFormOpen ? ' is-hidden' : ''}`}>
                <Button
                  id='signInButton'
                  variant="primary"
                  size="large"
                  className='home-hero-login-btn'
                  aria-expanded={!isCompact || mobileFormOpen}
                  aria-controls='auth-panel'
                  onClick={() => { revealAuthPanel('signIn') }}
                >
                  {t('words.login')}
                </Button>
                <Button
                  variant="outline"
                  size="large"
                  className='outlined-home-btn home-hero-signup'
                  onClick={openSignUp}
                >
                  {t('words.to_sign_up')}
                </Button>
              </div>
            </section>

            <aside
              className={`home-panel${mobileFormOpen ? ' home-panel--open' : ''}`}
              id='auth-panel'
              aria-hidden={isCompact && !mobileFormOpen}
              onClick={(event) => {
                if (!isCompact) return
                if ((event.target as HTMLElement).closest('.home-panel-card')) return
                hideMobileForm()
              }}
            >
              <div className='home-panel-slot'>
                <div className='home-panel-card'>
                {isCompact && (
                  <button type='button' className='auth-drawer-back home-panel-back' onClick={hideMobileForm}>
                    <LeftOutlined /> {t('devise.shared.links.back')}
                  </button>
                )}
                {panel === 'resetPassword' && (
                  <>
                    <PanelHeading
                      eyebrow={t('sentences.auth_reset_eyebrow')}
                      title={t('sentences.reset_password')}
                    />
                    <ResetPassword />
                    <p className='auth-switch'>
                      <button type='button' className='auth-text-link' onClick={() => { revealAuthPanel('signIn') }}>
                        {t('sentences.back_to_sign_in')}
                      </button>
                    </p>
                  </>
                )}
                {panel === 'signIn' && (
                  <>
                    {error && (
                      <Alert
                        message={error}
                        type="error"
                        className='home-panel-alert'
                      />
                    )}
                    <PanelHeading
                      eyebrow={t('sentences.auth_sign_in_eyebrow')}
                      title={t('sentences.connect')}
                    />
                    <AntForm
                      schema={SignInSchema}
                      object={object}
                      layout="vertical"
                      errors={fieldErrors}
                      onChange={handleChange}
                      onSubmit={onSubmit}
                      onFinishFailed={() => {
                        setError(t('sentences.auth_fields_required'))
                      }}
                      submitText={t('words.login')}
                      className='auth-form'
                      actionsWrapperProps={{
                        className: 'auth-form-actions'
                      }}
                      submitButtonProps={{
                        className: 'primary-btn'
                      }}
                    />
                    <p className='auth-switch'>
                      {t('sentences.no_account_yet')}{' '}
                      <button type='button' className='auth-text-link' onClick={openSignUp}>
                        {t('words.to_sign_up')}
                      </button>
                    </p>
                  </>
                )}
                </div>
              </div>
            </aside>
          </div>
        </div>

        <Drawer
          className='auth-signup-drawer'
          placement="right"
          closable={false}
          destroyOnClose
          onClose={closeSignUp}
          open={signUpOpen}
          width={isMobile ? '100%' : 640}
          height="100%"
          zIndex={1100}
          getContainer={() => document.body}
          maskStyle={{ background: 'var(--overlay-modal, rgba(0, 13, 27, 0.38))' }}
          title={
            <button type='button' className='auth-drawer-back' onClick={closeSignUp}>
              <LeftOutlined /> {t('devise.shared.links.back')}
            </button>
          }
        >
          <div className='auth-signup-drawer-stack'>
            <div className='auth-signup-logo'>
              <img className='auth-signup-logo-light' src={logo} width={203} height={113} alt="Qualimetrie" />
              <img className='auth-signup-logo-dark' src={whiteLogo} width={203} height={113} alt="Qualimetrie" />
            </div>
            <div className='auth-signup-drawer-inner'>
              <SignUp locale={locale} />
              <p className='auth-switch'>
                {t('sentences.already_have_account')}{' '}
                <button type='button' className='auth-text-link' onClick={closeSignUp}>
                  {t('words.login')}
                </button>
              </p>
            </div>
          </div>
        </Drawer>
      </Layout>
    </ConfigProvider>
  )
}

export default TranslationProvider(SignIn)
